import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { SearchDateRange } from '../../interfaces/SearchDateRange';
import { PERIODS_LIST } from '../../constants/search.constants';
import { UntypedFormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs/operators';
import { IDateRange } from '../../interfaces/IDateRange';
import { getDateRange, getEndOfDay } from '../../helpers/date-helpers';

@UntilDestroy()
@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateFilterComponent implements OnInit {
  @Input() period: UntypedFormControl;
  @Input() customPeriod: UntypedFormControl;
  @Input() isLoading: boolean;
  @Input() label: string = 'Date Range';

  @Output() tabEnter: EventEmitter<void> = new EventEmitter();
  @Output() dropdownOpened: EventEmitter<void> = new EventEmitter();
  @Output() dropdownClosed: EventEmitter<void> = new EventEmitter();
  @Output() dateRangeChanged: EventEmitter<IDateRange> = new EventEmitter();

  @ViewChild('periodDropdown') periodDropdown;
  @ViewChild('customPeriodDatepicker') customPeriodDatepicker;

  periodsList = [
    { id: SearchDateRange.Unspecified, name: 'All Time' },
    { id: SearchDateRange.Custom, name: 'Custom Range', value: undefined },
    ...PERIODS_LIST,
  ];

  constructor(private datePipe: DatePipe, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.customPeriod.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((val) => {
        if (!val) {
          this.resetCustomRange();
        } else if (val[0] && val[1]) {
          const custom = this.periodsList.find(
            (x) => x.id === SearchDateRange.Custom
          );
          custom.name = `Custom (${this.datePipe.transform(
            val[0],
            'M/d/yyyy'
          )} - ${this.datePipe.transform(val[1], 'M/d/yyyy')})`;
          (custom as any).value = { startDate: val[0], endDate: val[1] };
          this.customPeriodDatepicker.setValue(val.map((x) => new Date(x)));
        }
        this.cd.detectChanges();
      });
  }

  changePeriod(period): void {
    if (period && period.id === SearchDateRange.Custom) {
      setTimeout(() => this.customPeriodDatepicker.datepickerInstance?.open());
    } else {
      this.periodsList.find((x) => x.id === SearchDateRange.Custom).name =
        'Custom Range';
      this.resetCustomRange();
      this.dateRangeChanged.emit(getDateRange(period.id));
    }
  }

  customPeriodChanged(date): void {
    const custom = this.periodsList.find(
      (x) => x.id === SearchDateRange.Custom
    );
    custom.name = `Custom (${this.datePipe.transform(
      date.value[0],
      'M/d/yyyy'
    )} - ${this.datePipe.transform(date.value[1], 'M/d/yyyy')})`;
    this.periodDropdown.chooseOption(custom);
    (custom as any).value = {
      startDate: date.value[0],
      endDate: date.value[1],
    };
    this.customPeriod?.setValue(date?.value);
    this.dateRangeChanged.emit({startDate: date.value[0], endDate: getEndOfDay(date.value[1])});
  }

  checkDateRangeAfterClosed(): void {
    const custom = this.periodsList.find(
      (x) => x.id === SearchDateRange.Custom
    );
    if (custom.name === 'Custom Range') {
      this.periodDropdown.chooseOption(custom);
    }
  }

  private resetCustomRange(): void {
    const customRange = this.periodsList.find(
      (x) => x.id === SearchDateRange.Custom
    );
    customRange.name = 'Custom Range';
    (customRange as any).value = null;
    this.customPeriodDatepicker.reset();
  }

  now(): Date {
    return new Date();
  }
}
